import React, { useEffect } from 'react';
import { t } from '@/locales/strings';
import { useModalA11y } from '@/hooks/useModalA11y';
import { JOURNEY_PHASES } from '@/data/methodJourney';
import { DUR, EASE } from '@/design/tokens';
import { ACCENTS } from '@/components/method/accents';
import SkillGlyph from '@/components/method/SkillGlyph';

/**
 * ChapterCompleteOverlay — fires once when the user crosses into a
 * new chapter of the five-chapter arc. Different visual from the
 * streak milestone: centers a glyph (the completed chapter's verb)
 * instead of a number, and leads with the chapter's name.
 *
 * Parent owns open/close and navigation. Triggered by
 * useChapterMilestone() below.
 */
export default function ChapterCompleteOverlay({ phaseKey, lang = 'en', onClose, onReadLetter }) {
  const dialogRef = useModalA11y({ open: !!phaseKey, onClose });
  const phase = JOURNEY_PHASES.find((p) => p.key === phaseKey);

  // Gentle confirm ding + a ceremonial three-pulse vibrate on mount,
  // same shape as StreakMilestoneOverlay so the two surfaces feel
  // like siblings.
  useEffect(() => {
    if (!phaseKey) return;
    let cancelled = false;
    import('@/utils/sound').then((mod) => {
      if (!cancelled) mod.playConfirmDing?.();
    }).catch(() => {});
    try {
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate([40, 80, 40, 80, 60]);
      }
    } catch {}
    return () => { cancelled = true; };
  }, [phaseKey]);

  if (!phase) return null;
  const accent = ACCENTS[phase.accent] || ACCENTS.rose;
  const isFinal = phase.key === 'mastery';

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="chapter-complete-title"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 650,
        background: 'rgba(28, 14, 22, 0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
        paddingTop: 'max(20px, env(safe-area-inset-top))',
        paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
        paddingLeft: 'max(20px, env(safe-area-inset-left))',
        paddingRight: 'max(20px, env(safe-area-inset-right))',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        animation: `bb-chapter-fade ${DUR.med}ms ${EASE.standard}`,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 360,
          background:
            `linear-gradient(180deg, #fff 0%, ${accent.tintFaint} 60%, ${accent.tint} 100%)`,
          borderRadius: 28,
          padding: '36px 28px 24px',
          boxShadow: '0 30px 80px -20px rgba(45,31,38,.5)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 14,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          animation: `bb-chapter-pop ${DUR.long}ms ${EASE.spring}`,
        }}
      >
        <div aria-hidden="true" style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background:
            `radial-gradient(ellipse 60% 40% at 50% 40%, ${accent.tint} 0%, transparent 70%)`,
        }} />

        <div style={{
          font: '600 10px/1 var(--font-ui, Manrope)',
          letterSpacing: '0.32em',
          textTransform: 'uppercase',
          color: accent.inkStrong,
          zIndex: 1,
        }}>
          {t(isFinal ? 'chapter_complete_final_eyebrow' : 'chapter_complete_eyebrow', lang)}
        </div>

        <div style={{
          width: 88, height: 88,
          borderRadius: '50%',
          background: '#fff',
          border: `2px solid ${accent.stroke}`,
          display: 'grid',
          placeItems: 'center',
          zIndex: 1,
          boxShadow: `0 12px 28px -16px ${accent.shadow}`,
        }}>
          <SkillGlyph skill={phase.verbKey} size={48} color={accent.inkStrong} />
        </div>

        <h2 id="chapter-complete-title" style={{
          font: '500 24px/1.15 var(--font-title, Fraunces), serif',
          color: 'var(--ink, #2d1f26)',
          margin: 0,
          letterSpacing: '-0.01em',
          zIndex: 1,
          textWrap: 'balance',
        }}>
          {t(isFinal ? 'chapter_complete_final_title' : 'chapter_complete_title', lang)
            .replace('{chapter}', t(phase.titleKey, lang))}
        </h2>

        <p style={{
          font: '400 14px/1.5 var(--font-body, Manrope)',
          fontStyle: 'italic',
          color: 'var(--ink-dim, #6b5560)',
          margin: 0,
          maxWidth: 280,
          zIndex: 1,
          textWrap: 'balance',
        }}>
          {t(`chapter_complete_sub_${phase.key}`, lang)}
        </p>

        <div style={{ display: 'flex', gap: 10, marginTop: 8, width: '100%', zIndex: 1 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              minHeight: 48,
              borderRadius: 999,
              border: '1px solid color-mix(in srgb, var(--ink, #2d1f26) 12%, transparent)',
              background: 'rgba(255,255,255,0.6)',
              color: 'var(--ink, #2d1f26)',
              font: '600 14px/1 var(--font-ui, Manrope)',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {t('chapter_complete_continue', lang)}
          </button>
          <button
            type="button"
            onClick={onReadLetter}
            style={{
              flex: 1.3,
              minHeight: 48,
              borderRadius: 999,
              border: 'none',
              background: `linear-gradient(180deg, ${accent.tint} 0%, ${accent.inkStrong} 180%)`,
              color: '#fff',
              font: '600 14px/1 var(--font-ui, Manrope)',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              boxShadow: `0 10px 24px -10px ${accent.shadow}`,
            }}
          >
            {t('chapter_complete_read_letter', lang)}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes bb-chapter-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes bb-chapter-pop {
          0%   { transform: scale(0.86) translateY(10px); opacity: 0; }
          60%  { transform: scale(1.02) translateY(0);     opacity: 1; }
          100% { transform: scale(1)    translateY(0);     opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ── useChapterMilestone hook ─────────────────────────────────────
// Detects "a new chapter was just entered" based on userProgress and
// returns the prior chapter's key (the one to celebrate as "complete").
// Same hiatus-return handling as useStreakMilestone: only the most
// recent un-celebrated chapter fires, silently marking older ones as
// seen so a 200-week veteran doesn't get chain-fired five overlays.

const STORAGE_KEY = 'bb_chapter_milestones_seen';

function readSeen() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}
function writeSeen(arr) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(arr)); } catch {}
}

/**
 * @param {{ current_year?: number, current_week?: number } | null} userProgress
 * @returns {{ phaseKey: string|null, dismiss: () => void }}
 */
export function useChapterMilestone(userProgress) {
  const [phaseKey, setPhaseKey] = React.useState(null);

  React.useEffect(() => {
    const year = userProgress?.current_year;
    const week = userProgress?.current_week;
    if (!Number.isFinite(year) || !Number.isFinite(week)) return;

    // A chapter is "complete" from the user's perspective the moment
    // she has moved past its last week. Build the list of completed
    // chapter keys deterministically from her current position.
    const completed = [];
    for (const p of JOURNEY_PHASES) {
      const [, end] = p.weekRange;
      if (p.year < year) completed.push(p.key);
      else if (p.year === year && week > end) completed.push(p.key);
    }
    if (!completed.length) return;

    const seen = new Set(readSeen());
    const unseen = completed.filter((k) => !seen.has(k));
    if (!unseen.length) return;

    // Fire the most-recent unseen one. Mark older unseen as seen so
    // a returning veteran only gets one moment, not four in a row.
    const latest = unseen[unseen.length - 1];
    setPhaseKey(latest);
    if (unseen.length > 1) {
      for (const older of unseen.slice(0, -1)) seen.add(older);
      writeSeen(Array.from(seen));
    }
  }, [userProgress?.current_year, userProgress?.current_week]);

  const dismiss = React.useCallback(() => {
    if (!phaseKey) return;
    const seen = new Set(readSeen());
    seen.add(phaseKey);
    writeSeen(Array.from(seen));
    setPhaseKey(null);
  }, [phaseKey]);

  return { phaseKey, dismiss };
}
